import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import { useBuatProspek, useProspek } from '../../../domains/prospect/api'
import { istilah } from '../../../shared/lib/istilah'
import { sejak } from '../../../shared/lib/waktu'
import {
  AreaTeks, Baris, Bendera, BilahTabel, Body, Chip, Gagal, Isian, KepalaHalaman, Kosong,
  Memuat, Panel, Sub, Tabel, Tombol,
} from '../../../shared/ui'

const TAHAP_NADA = {
  collecting: 'warn', profiled: 'ok', scheduling: 'ok', scheduled: 'ok', exploring: 'idle',
}

// Kelima kolom bawaan, urut seperti di halaman detail. SEMUANYA WAJIB: rute pembuatnya
// langsung menaruh prospek manual di tahap `profiled` ("semua field wajib harus terisi
// sekaligus di sini, beda dari alur AI yang bertahap") — prospek setengah terisi yang
// berstatus lengkap adalah tahap yang berbohong.
const MEDAN = [
  ['company_name', 'prospek.perusahaan', 'text'],
  ['company_address', 'prospek.alamat', 'alamat'],
  ['pic_name', 'prospek.pic', 'text'],
  ['pic_email', 'prospek.email', 'email'],
  ['pic_phone', 'prospek.telepon', 'tel'],
]

/** Badan `POST /dashboard/prospects` dari isian formulir — dipangkas. MURNI. */
export function badanProspek(f) {
  return Object.fromEntries(MEDAN.map(([k]) => [k, String(f?.[k] ?? '').trim()]))
}

/** Kelima isian terisi (bukan cuma spasi). MURNI. */
export function prospekLengkap(f) {
  return Object.values(badanProspek(f)).every(Boolean)
}

/**
 * TAMBAH PROSPEK MANUAL — prospek dari luar Instagram.
 *
 * Tombol "Tambah manual" di halaman ini ada sejak layar pertama dan tidak pernah
 * tersambung ke apa pun: tanpa `onClick`, jadi ditekan pun tidak terjadi apa-apa
 * (laporan operator 22 September). Rutenya sudah ada di backend sejak repo lama.
 *
 * SESUDAH DISIMPAN, HALAMAN PROSPEKNYA DIBUKA. Prospek manual hampir selalu dimasukkan
 * karena meetingnya sudah atau akan terjadi, dan tombol "Jadwalkan meeting" ada di sana.
 *
 * Komponen tersendiri supaya uji render bisa membukanya — di dalam halaman ia hanya
 * muncul lewat state, dan renderToString tidak pernah mengisinya (XXIII-1).
 */
export function FormTambahProspek({ t, simpan, tutup, selesai }) {
  const [f, setF] = useState(() => Object.fromEntries(MEDAN.map(([k]) => [k, ''])))
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }))
  const bisa = prospekLengkap(f) && !simpan.isPending
  return (
    <div className="flex flex-col gap-2.5 max-w-[52ch]">
      {MEDAN.map(([k, label, jenis]) => (
        <label key={k} className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
            {t(label)}
          </span>
          {jenis === 'alamat'
            ? <AreaTeks baris={2} value={f[k]} onChange={(e) => set(k, e.target.value)} />
            : <Isian type={jenis} value={f[k]} onChange={(e) => set(k, e.target.value)} />}
        </label>
      ))}
      <span className="flex gap-2">
        <Tombol kecil disabled={!bisa}
                onClick={() => simpan.mutate(badanProspek(f), { onSuccess: selesai })}>
          {simpan.isPending ? t('umum.menyimpan') : t('prospek.simpanProspek')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
      <Sub>{t('prospek.tambahKet')}</Sub>
    </div>
  )
}

export default function Prospek() {
  const { t, locale } = useBahasa()
  const pergi = useNavigate()
  const [cari, setCari] = useState('')
  const [tahap, setTahap] = useState('')
  const [buka, setBuka] = useState(false)
  const buat = useBuatProspek()

  const q = useProspek()
  const semua = useMemo(() => {
    const daftar = Array.isArray(q.data) ? q.data : q.data?.items ?? []
    const c = cari.trim().toLowerCase()
    return daftar.filter((p) => {
      if (tahap && p.stage !== tahap) return false
      if (!c) return true
      return [p.company_name, p.pic_name, p.sender_username, p.pic_email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(c))
    })
  }, [q.data, cari, tahap])

  const opsiTahap = useMemo(() => {
    const daftar = Array.isArray(q.data) ? q.data : q.data?.items ?? []
    const unik = [...new Set(daftar.map((p) => p.stage).filter(Boolean))]
    return [{ nilai: '', label: t('umum.semua') }, ...unik.map((s) => ({ nilai: s, label: s }))]
  }, [q.data, t])

  return (
    <>
      <KepalaHalaman
        judul={t('prospek.judul')}
        keterangan={t('prospek.keterangan')}
        aksi={<Tombol disabled={buka} onClick={() => setBuka(true)}>{t('prospek.tambah')}</Tombol>}
      />

      {buka && (
        <Panel judul={t('prospek.tambahJudul')}>
          <Body>
            <FormTambahProspek
              t={t}
              simpan={buat}
              tutup={() => { buat.reset(); setBuka(false) }}
              selesai={(data) => pergi(`/prospek/${data.id}`)}
            />
          </Body>
        </Panel>
      )}

      <Panel>
        {/*
          Penyaringan dilakukan di KLIEN, bukan di server — karena rute
          /dashboard/prospects belum menerima parameter kueri. Itu benar selama
          barisnya masih puluhan; begitu ratusan, penyaringnya harus pindah ke
          server dan pemanggilan ini ikut berubah. Ditulis di sini supaya
          perpindahannya tidak jadi kejutan.
        */}
        <BilahTabel
          cari={{ nilai: cari, set: setCari, placeholder: t('umum.cari') }}
          saring={{ nilai: tahap, set: setTahap, opsi: opsiTahap }}
          ringkas={q.data ? t('umum.dari', { mulai: semua.length ? 1 : 0, akhir: semua.length, total: (Array.isArray(q.data) ? q.data : q.data?.items ?? []).length }) : null}
        />

        {q.isLoading && <Memuat />}
        {q.isError && <Gagal galat={q.error} ulangi={q.refetch} />}

        {q.data && semua.length === 0 && (
          <Kosong
            judul={t('prospek.kosong')}
            keterangan={cari || tahap ? null : undefined}
            aksi={buka ? null : (
              <Tombol jenis="hantu" onClick={() => setBuka(true)}>{t('prospek.tambah')}</Tombol>
            )}
          />
        )}

        {q.data && semua.length > 0 && (
          <Tabel
            kepala={[
              t('prospek.perusahaan'), t('prospek.asal'), t('prospek.tahap'),
              t('prospek.ditangani'), t('prospek.bergerak'),
            ]}
          >
            {semua.map((p) => (
              <Baris key={p.id} onClick={() => pergi(`/prospek/${p.id}`)}>
                <td>
                  <span className="font-semibold text-ink">{p.company_name || '—'}</span>
                  {p.sender_username && (
                    <span className="block text-[11px] text-ink3 font-mono">@{p.sender_username}</span>
                  )}
                </td>
                <td>
                  <Chip nada="idle" titik={false}>{p.source === 'manual' ? 'Manual' : 'Instagram'}</Chip>
                </td>
                <td><Chip nada={TAHAP_NADA[p.stage] ?? 'idle'}>{istilah(t, 'tahap', p.stage)}</Chip></td>
                <td>{p.handled_by || p.assigned_to_name || <span className="text-ink3">—</span>}</td>
                <td className="font-mono text-[11.5px] text-ink2">{sejak(p.updated_at, locale)}</td>
              </Baris>
            ))}
          </Tabel>
        )}
      </Panel>
    </>
  )
}
